import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Card, CardContent } from '@/components/ui/card';
import {
  History,
  UserPlus,
  RotateCcw,
  CheckCircle,
  Award,
  Calendar,
  User,
  Activity,
} from 'lucide-react';
import type { TaskHistoryWithUser } from '@shared/schema';

interface TaskHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  taskId: string;
  taskTitle: string;
}

export default function TaskHistoryModal({
  isOpen,
  onClose,
  taskId,
  taskTitle,
}: TaskHistoryModalProps) {
  const { data: history = [], isLoading } = useQuery<TaskHistoryWithUser[]>({
    queryKey: [`/api/tasks/${taskId}/history`],
    enabled: isOpen && !!taskId,
  });

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'created':
        return <Calendar className="w-4 h-4 text-blue-600" />;
      case 'assigned':
        return <UserPlus className="w-4 h-4 text-purple-600" />;
      case 'status_changed':
        return <RotateCcw className="w-4 h-4 text-orange-600" />;
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'approved':
        return <Award className="w-4 h-4 text-yellow-600" />;
      case 'commented':
        return <Activity className="w-4 h-4 text-cyan-600" />;
      default:
        return <Activity className="w-4 h-4 text-gray-600" />;
    }
  };

  const getActionLabel = (action: string) => {
    switch (action) {
      case 'created':
        return 'Task Created';
      case 'assigned':
        return 'Task Assigned';
      case 'status_changed':
        return 'Status Changed';
      case 'completed':
        return 'Task Completed';
      case 'approved':
        return 'Task Approved';
      case 'commented':
        return 'Comment Added';
      default:
        return action.charAt(0).toUpperCase() + action.slice(1);
    }
  };

  const getActionDescription = (entry: TaskHistoryWithUser) => {
    try {
      const details = entry.details ? JSON.parse(entry.details) : {};
      const userName = entry.user
        ? `${entry.user.firstName} ${entry.user.lastName}`
        : 'System';

      switch (entry.action) {
        case 'created':
          return `${userName} created this task`;
        case 'assigned':
          return `Task reassigned from ${details.previousAssignee} to ${details.newAssignee}`;
        case 'status_changed':
          return `${userName} changed status: ${details.statusChange}`;
        case 'completed':
          return `${userName} marked task as complete`;
        case 'approved':
          return `${details.approver} approved the completed task`;
        case 'commented':
          return `${userName} commented: ${details.content ? '"' + details.content + '"' : ''}`;
        default:
          return `${userName} performed action: ${entry.action}`;
      }
    } catch (error) {
      const userName = entry.user
        ? `${entry.user.firstName} ${entry.user.lastName}`
        : 'System';
      return `${userName} performed action: ${entry.action}`;
    }
  };

  const getActionBadgeColor = (action: string) => {
    switch (action) {
      case 'created':
        return 'bg-blue-100 text-blue-800 hover:bg-blue-100';
      case 'assigned':
        return 'bg-purple-100 text-purple-800 hover:bg-purple-100';
      case 'status_changed':
        return 'bg-orange-100 text-orange-800 hover:bg-orange-100';
      case 'completed':
        return 'bg-green-100 text-green-800 hover:bg-green-100';
      case 'approved':
        return 'bg-yellow-100 text-yellow-800 hover:bg-yellow-100';
      default:
        return 'bg-gray-100 text-gray-800 hover:bg-gray-100';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <History className="w-5 h-5 mr-2" />
            Task History
          </DialogTitle>
          <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
            {taskTitle}
          </p>
        </DialogHeader>

        <div
          className="flex-1 overflow-y-auto pr-2"
          data-testid="task-history-content"
        >
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">Loading history...</span>
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <History className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>No history available for this task.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {history.map((entry, index) => (
                <Card
                  key={entry.id}
                  className="border-l-4 border-l-gray-200 dark:border-l-gray-700"
                >
                  <CardContent className="p-4">
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 mt-1">
                        {getActionIcon(entry.action)}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-2">
                          <Badge
                            variant="secondary"
                            className={getActionBadgeColor(entry.action)}
                          >
                            {getActionLabel(entry.action)}
                          </Badge>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {new Date(entry.createdAt!).toLocaleString(
                              'en-US',
                              {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              }
                            )}
                          </span>
                        </div>

                        <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                          {getActionDescription(entry)}
                        </p>

                        {entry.user && (
                          <div className="flex items-center space-x-2">
                            <Avatar className="w-6 h-6">
                              <AvatarImage
                                src={entry.user.profileImageUrl || undefined}
                                alt={`${entry.user.firstName} ${entry.user.lastName}`}
                              />
                              <AvatarFallback className="text-xs bg-gray-100 dark:bg-gray-800">
                                {entry.user.firstName?.[0]}
                                {entry.user.lastName?.[0]}
                              </AvatarFallback>
                            </Avatar>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {entry.user.firstName} {entry.user.lastName}
                            </span>
                            <Badge variant="outline" className="text-xs">
                              {entry.user.role?.replace('_', ' ')}
                            </Badge>
                          </div>
                        )}

                        {entry.oldValue &&
                          entry.newValue &&
                          entry.action !== 'assigned' && (
                            <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 rounded p-2">
                              <span className="font-medium">Change:</span>
                              <span className="text-red-600 dark:text-red-400">
                                {entry.oldValue}
                              </span>
                              <span className="mx-2">→</span>
                              <span className="text-green-600 dark:text-green-400">
                                {entry.newValue}
                              </span>
                            </div>
                          )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
